// AGC: MachineDetail - Detailed view for a single machine
// AGC: Shows machine info, usage timeline, employee summary, and maintenance records
// AGC: Includes preventive maintenance configuration

import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from '../../hooks/useTranslation'
import { useAuth } from '../../hooks/useAuth'
import { useMachinePermissions } from '../../hooks/useRBAC'
import { getMachine, getMachineWorkLogs, updateMachine } from '../../services/machinesService'
import {
  getMachineMaintenanceRecords,
  createMaintenanceRecord,
  deleteMaintenanceRecord,
  updatePreventiveMaintenanceConfig,
} from '../../services/machineMaintenanceService'
import type { MachineInstance } from '../../types/machineInstance'
import type { MachineWorkLog } from '../../types/machineWorkLog'
import type { MachineMaintenanceRecord, CreateMaintenanceRecordRequest } from '../../types/machineMaintenance'
import {
  getMachineStatusColor,
  getMachineStatusLabel,
  formatHorimeterHours,
  formatHectaresWorked,
  calculateMaintenanceStatus,
} from '../../types/machineInstance'
import { getMaintenanceTypeColor } from '../../types/machineMaintenance'
import { TopBar } from '../shared'
import { UsageTimeline } from './UsageTimeline'
import { EmployeeSummary } from './EmployeeSummary'
import { MaintenanceForm } from './MaintenanceForm'
import { PreventiveMaintenanceCard } from './PreventiveMaintenanceCard'
import { CustomMaintenanceModal, type CustomMaintenanceConfig } from './CustomMaintenanceModal'
import { MACHINE_CATEGORIES, getMachineIcon, isKnownBrand, type MachineCategory } from '../../types/machine'
import {
  Loader2,
  AlertTriangle,
  Clock,
  MapPin,
  User,
  Calendar,
  Settings,
  Plus,
  Wrench,
  ChevronDown,
  ChevronUp,
  Trash2,
  Edit3,
  DollarSign,
  Pencil,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { MachineIconPicker } from './MachineIconPicker'

interface MachineDetailProps {
  machineId: string
  onBack: () => void
  onNavigate: (screen: string, params?: any) => void
}

/**
 * AGC: MachineDetail - Complete machine detail page
 * AGC: Features:
 * AGC: - Machine info header with status
 * AGC: - Usage summary (hours, employees, lots)
 * AGC: - Usage timeline with work logs
 * AGC: - Maintenance records list
 * AGC: - Preventive maintenance configuration
 */
export const MachineDetail: React.FC<MachineDetailProps> = ({
  machineId,
  onBack,
  onNavigate,
}) => {
  const { t } = useTranslation()
  const { getAuthToken, user } = useAuth()
  const { canEdit, canViewCosts } = useMachinePermissions()

  // AGC: Data states
  const [machine, setMachine] = useState<MachineInstance | null>(null)
  const [workLogs, setWorkLogs] = useState<MachineWorkLog[]>([])
  const [maintenanceRecords, setMaintenanceRecords] = useState<MachineMaintenanceRecord[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingMaintenance, setIsLoadingMaintenance] = useState(true)

  // AGC: Icon picker state
  const [showIconPicker, setShowIconPicker] = useState(false)

  // AGC: UI states
  const [showMaintenanceForm, setShowMaintenanceForm] = useState(false)
  const [showCustomMaintenanceModal, setShowCustomMaintenanceModal] = useState(false)
  const [isSubmittingMaintenance, setIsSubmittingMaintenance] = useState(false)
  const [isSubmittingCustomConfig, setIsSubmittingCustomConfig] = useState(false)
  const [expandedSections, setExpandedSections] = useState({
    usageTimeline: true,
    employees: true,
    preventive: true,
    maintenance: true,
  })

  // AGC: Load data on mount
  useEffect(() => {
    if (!machineId) {
      console.error('AGC: MachineDetail - machineId is required')
      setIsLoading(false)
      return
    }
    loadMachineData()
  }, [machineId])

  const loadMachineData = async () => {
    if (!machineId) {
      console.error('AGC: Cannot load machine data - machineId is empty')
      return
    }

    try {
      setIsLoading(true)
      const token = await getAuthToken()
      
      if (!token) {
        console.error('AGC: No auth token available')
        toast.error('Authentication required. Please log in again.')
        return
      }

      console.log('AGC: Loading machine data for:', machineId)
      
      // AGC: Load machine and work logs in parallel
      const [machineData, logsData] = await Promise.all([
        getMachine(machineId, token),
        getMachineWorkLogs(machineId, token),
      ])
      
      setMachine(machineData)
      setWorkLogs(logsData)

      // AGC: Load maintenance records separately
      setIsLoadingMaintenance(true)
      const maintenanceData = await getMachineMaintenanceRecords(machineId, token)
      setMaintenanceRecords(maintenanceData)
      setIsLoadingMaintenance(false)
    } catch (error) {
      console.error('AGC: Error loading machine:', error)
      toast.error(t('machines.errors.loadFailed'))
    } finally {
      setIsLoading(false)
    }
  }

  // AGC: Calculate usage summaries
  const usageSummary = useMemo(() => {
    const uniqueEmployees = new Set(workLogs.map((log) => log.operatorUserId))
    const uniqueLots = new Set(workLogs.map((log) => log.lotId).filter(Boolean))
    const totalHoursFromLogs = workLogs.reduce((sum, log) => sum + log.hoursUsed, 0)

    return {
      employeeCount: uniqueEmployees.size,
      lotCount: uniqueLots.size,
      totalHoursFromLogs,
    }
  }, [workLogs])

  // AGC: Handle adding maintenance record
  const handleAddMaintenance = async (data: CreateMaintenanceRecordRequest) => {
    try {
      setIsSubmittingMaintenance(true)
      const token = await getAuthToken()
      
      await createMaintenanceRecord(machineId, data, token)
      toast.success(t('machines.detail.maintenanceAdded'))
      
      setShowMaintenanceForm(false)
      
      await loadMachineData()
      window.dispatchEvent(new CustomEvent('refresh-machines'))
    } catch (error) {
      console.error('AGC: Error creating maintenance:', error)
      toast.error(t('machines.errors.maintenanceCreateFailed'))
    } finally {
      setIsSubmittingMaintenance(false)
    }
  }

  // AGC: Handle deleting maintenance record
  const handleDeleteMaintenance = async (recordId: string) => {
    if (!confirm('¿Eliminar este registro de mantenimiento?')) return

    try {
      const token = await getAuthToken()
      await deleteMaintenanceRecord(machineId, recordId, token)
      toast.success(t('machines.detail.maintenanceDeleted'))
      
      // AGC: Remove from local state
      setMaintenanceRecords((prev) => prev.filter((r) => r.id !== recordId))
    } catch (error) {
      console.error('AGC: Error deleting maintenance:', error)
      toast.error(t('machines.errors.maintenanceDeleteFailed'))
    }
  }

  // AGC: Handle updating preventive maintenance config
  const handleUpdateConfig = async (intervalHours: number) => {
    if (!machine) return

    try {
      const token = await getAuthToken()
      await updatePreventiveMaintenanceConfig(machineId, {
        intervalHours,
        lastMaintenanceHours: machine.lastMaintenanceHours || machine.horimeterHours,
        lastMaintenanceDate: machine.lastMaintenanceDate || new Date().toISOString().split('T')[0],
      }, token)
      
      toast.success(t('machines.detail.configUpdated'))
      await loadMachineData()
    } catch (error) {
      console.error('AGC: Error updating config:', error)
      toast.error(t('machines.errors.configUpdateFailed'))
    }
  }

  // AGC: Handle saving custom maintenance configuration from modal
  const handleSaveCustomConfig = async (config: CustomMaintenanceConfig) => {
    if (!machine) return

    try {
      setIsSubmittingCustomConfig(true)
      const token = await getAuthToken()
      
      // AGC: Build description from selected types if not provided
      const typeLabels = config.maintenanceTypes.map(typeId => 
        t(`machines.detail.maintenanceType.${typeId}`)
      ).join(', ')
      
      const description = config.description || typeLabels
      
      await updatePreventiveMaintenanceConfig(machineId, {
        intervalHours: config.hours,
        // AGC: Use current hours as baseline so the alarm triggers after X more hours
        lastMaintenanceHours: machine.lastMaintenanceHours ?? machine.horimeterHours,
        lastMaintenanceDate: machine.lastMaintenanceDate || new Date().toISOString().split('T')[0],
        maintenanceDescription: description,
      }, token)
      
      toast.success(t('machines.detail.configUpdated'))
      setShowCustomMaintenanceModal(false)
      await loadMachineData()
    } catch (error) {
      console.error('AGC: Error updating custom config:', error)
      toast.error(t('machines.errors.configUpdateFailed'))
    } finally {
      setIsSubmittingCustomConfig(false)
    }
  }

  // AGC: Handle navigating to lot
  const handleLotClick = (lotId: string) => {
    onNavigate('lot-page', { selectedFieldId: lotId })
  }

  // AGC: Toggle section expansion
  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }))
  }

  // AGC: Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <TopBar title={t('machines.machine')} onBack={onBack} />
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      </div>
    )
  }

  // AGC: Not found state
  if (!machine) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <TopBar title={t('machines.machine')} onBack={onBack} />
        <div className="text-center py-12">
          <p className="text-gray-600 dark:text-gray-400">Máquina não encontrada</p>
        </div>
      </div>
    )
  }

  const machineWithStatus = calculateMaintenanceStatus(machine)
  const { maintenanceStatus, maintenanceDaysUntilDue, maintenanceHoursUntilDue } = machineWithStatus
  const statusColor = getMachineStatusColor(machine.status)
  const statusLabel = getMachineStatusLabel(machine.status)
  const DETAIL_CAT_NORMALIZE: Record<string, string> = {
    tractor: 'trator', harvester: 'colheitadeira', sprayer: 'pulverizador',
    planter: 'plantadeira', spreader: 'distribuidor', implement: 'implemento',
    truck: 'transporte', drone: 'outros',
  }
  const normalizedCat = DETAIL_CAT_NORMALIZE[machine.category?.toLowerCase()] || machine.category
  const categoryInfo = MACHINE_CATEGORIES.find((c) => c.id === normalizedCat)
  const machineIconSrc = getMachineIcon(machine.category, machine.brand, machine.specs, machine.customIconPath, machine.name, machine.brandColor)

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-24">
      <TopBar title={machine.name} onBack={onBack} />

      <div className="px-4 py-4 space-y-4">
        {/* AGC: Machine Header Card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                {machine.name}
              </h2>
              <div className="flex items-center gap-2 flex-wrap">
                <span
                  className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-medium text-white ${statusColor} rounded-lg flex-shrink-0`}
                >
                  {statusLabel}
                </span>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {machine.brand && `${machine.brand} `}
                  {machine.model && machine.model}
                  {machine.year && ` (${machine.year})`}
                </p>
              </div>
              {categoryInfo && (
                <p className="text-xs font-medium text-emerald-600 dark:text-emerald-400 mt-1">
                  {categoryInfo.name}
                </p>
              )}
            </div>
            <div className="relative flex-shrink-0 ml-3 overflow-visible">
              <button
                onClick={() => setShowIconPicker(true)}
                className="w-[80px] h-[80px] flex items-center justify-center rounded-xl bg-amber-50 dark:bg-gray-700 border border-amber-200/50 active:scale-95 transition-transform"
              >
                <img src={machineIconSrc} alt={categoryInfo?.name || machine.category} className="w-full h-full object-contain p-1" />
              </button>
              <div className="absolute bottom-0 right-0 w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center shadow-md border-2 border-white dark:border-gray-800 pointer-events-none">
                <Pencil className="w-3 h-3 text-white" />
              </div>
            </div>
          </div>

          {/* AGC: Operational Metrics */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
              <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400 mb-1">
                <Clock className="w-4 h-4" />
                <span className="text-xs font-medium">{t('machines.detail.horimeter')}</span>
              </div>
              <p className="text-lg font-bold text-gray-900 dark:text-white">
                {formatHorimeterHours(machine.horimeterHours)}
              </p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
              <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400 mb-1">
                <User className="w-4 h-4" />
                <span className="text-xs font-medium">{t('machines.detail.employees')}</span>
              </div>
              <p className="text-lg font-bold text-gray-900 dark:text-white">
                {usageSummary.employeeCount}
              </p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
              <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400 mb-1">
                <MapPin className="w-4 h-4" />
                <span className="text-xs font-medium">{t('machines.detail.lots')}</span>
              </div>
              <p className="text-lg font-bold text-gray-900 dark:text-white">
                {usageSummary.lotCount}
              </p>
            </div>
          </div>
        </div>

        {/* AGC: Maintenance Status Alert */}
        {(maintenanceStatus === 'due_soon' || maintenanceStatus === 'overdue') && (
          <div
            className={`rounded-2xl p-4 border-2 ${
              maintenanceStatus === 'overdue'
                ? 'bg-red-50 dark:bg-red-900/20 border-red-500'
                : 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-500'
            }`}
          >
            <div className="flex items-start gap-3">
              <AlertTriangle
                className={`w-5 h-5 mt-0.5 ${
                  maintenanceStatus === 'overdue' ? 'text-red-500' : 'text-yellow-500'
                }`}
              />
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                  {maintenanceStatus === 'overdue'
                    ? t('machines.detail.maintenanceConfig.overdue', { hours: Math.abs(maintenanceHoursUntilDue || 0).toFixed(0) })
                    : t('machines.detail.maintenanceConfig.dueIn', { hours: (maintenanceHoursUntilDue || 0).toFixed(0) })}
                </h3>
                <button
                  onClick={() => setShowMaintenanceForm(true)}
                  className="text-sm font-medium text-emerald-600 dark:text-emerald-400 hover:underline"
                >
                  {t('machines.detail.addMaintenance')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* AGC: Employee Summary Section */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <button
            onClick={() => toggleSection('employees')}
            className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
          >
            <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <User className="w-5 h-5 text-blue-500" />
              {t('machines.detail.employeesUsed')} ({usageSummary.employeeCount})
            </h3>
            {expandedSections.employees ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>

          {expandedSections.employees && (
            <div className="px-4 pb-4">
              <EmployeeSummary workLogs={workLogs} isLoading={isLoading} />
            </div>
          )}
        </div>

        {/* AGC: Usage Timeline Section */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <button
            onClick={() => toggleSection('usageTimeline')}
            className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
          >
            <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Calendar className="w-5 h-5 text-purple-500" />
              {t('machines.detail.usageHistory')} ({workLogs.length})
            </h3>
            {expandedSections.usageTimeline ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>

          {expandedSections.usageTimeline && (
            <div className="px-4 pb-4">
              <UsageTimeline
                workLogs={workLogs}
                isLoading={isLoading}
                onLotClick={handleLotClick}
                maxItems={10}
                showViewAll={workLogs.length > 10}
              />
            </div>
          )}
        </div>

        {/* AGC: Preventive Maintenance Section - Standard intervals by machine type */}
        <PreventiveMaintenanceCard
          machineId={machine.id}
          category={(machine.category || 'outros') as MachineCategory}
          currentHours={machine.horimeterHours || 0}
          lastMaintenanceHours={machine.lastMaintenanceHours}
          activeIntervalIds={machine.activeMaintenanceIntervals}
          onConfigureClick={() => setShowCustomMaintenanceModal(true)}
          onToggleInterval={async (intervalId, isActive) => {
            try {
              const token = await getAuthToken()
              const currentActive = machine.activeMaintenanceIntervals || []
              const newActive = isActive
                ? [...currentActive, intervalId]
                : currentActive.filter((id: string) => id !== intervalId)
              
              await updatePreventiveMaintenanceConfig(machineId, {
                activeMaintenanceIntervals: newActive,
              }, token)
              
              toast.success(isActive ? 'Intervalo ativado' : 'Intervalo desativado')
              await loadMachineData()
            } catch (error) {
              console.error('AGC: Error toggling interval:', error)
              toast.error('Erro ao atualizar intervalo')
            }
          }}
          canEdit={canEdit}
        />

        {/* AGC: Maintenance Records Section */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <button
            onClick={() => toggleSection('maintenance')}
            className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
          >
            <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Wrench className="w-5 h-5 text-amber-500" />
              {t('machines.detail.maintenanceRecords')} ({maintenanceRecords.length})
            </h3>
            {expandedSections.maintenance ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>

          {expandedSections.maintenance && (
            <div className="px-4 pb-4">
              {/* AGC: Add Maintenance Button */}
              {canEdit && (
                <button
                  onClick={() => setShowMaintenanceForm(true)}
                  className="w-full mb-4 py-2.5 border-2 border-dashed border-emerald-300 dark:border-emerald-600 text-emerald-600 dark:text-emerald-400 rounded-lg font-medium hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  {t('machines.detail.addMaintenance')}
                </button>
              )}

              {/* AGC: Maintenance Records List */}
              {isLoadingMaintenance ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : maintenanceRecords.length > 0 ? (
                <div className="space-y-3">
                  {maintenanceRecords.map((record) => (
                    <div
                      key={record.id}
                      className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span
                            className={`px-2 py-0.5 text-xs font-medium text-white rounded ${getMaintenanceTypeColor(
                              record.type
                            )}`}
                          >
                            {t(`machines.detail.maintenanceType.${record.type}`)}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {new Date(record.date).toLocaleDateString('pt-BR')}
                          </span>
                        </div>
                        {canEdit && (
                          <button
                            onClick={() => handleDeleteMaintenance(record.id)}
                            className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>

                      <p className="text-sm text-gray-900 dark:text-white mb-1">
                        {record.description}
                      </p>

                      <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {record.hoursAtMaintenance}h
                        </span>
                        {record.cost && canViewCosts && (
                          <span className="flex items-center gap-1">
                            <DollarSign className="w-3 h-3" />
                            R$ {record.cost.toFixed(2)}
                          </span>
                        )}
                        {record.provider && (
                          <span className="truncate">{record.provider}</span>
                        )}
                      </div>

                      {record.notes && (
                        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 italic">
                          {record.notes}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Wrench className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
                  <p className="text-gray-600 dark:text-gray-400 font-medium">
                    {t('machines.detail.noMaintenanceRecords')}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                    {t('machines.detail.noMaintenanceRecordsDescription')}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* AGC: Edit Button */}
        {canEdit && (
          <button
            onClick={() => onNavigate('machine-form', { machineId: machine.id })}
            className="w-full py-3 bg-emerald-600 dark:bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 dark:hover:bg-emerald-500 transition-colors flex items-center justify-center gap-2"
          >
            <Edit3 className="w-4 h-4" />
            Editar Máquina
          </button>
        )}
      </div>

      {/* AGC: Maintenance Form Modal */}
      {showMaintenanceForm && machine && (
        <MaintenanceForm
          machine={machine}
          onSave={handleAddMaintenance}
          onCancel={() => setShowMaintenanceForm(false)}
          isSubmitting={isSubmittingMaintenance}
        />
      )}

      {/* AGC: Custom Maintenance Configuration Modal */}
      <CustomMaintenanceModal
        isOpen={showCustomMaintenanceModal}
        onClose={() => setShowCustomMaintenanceModal(false)}
        onSave={handleSaveCustomConfig}
        currentHours={machine?.horimeterHours || 0}
        initialHours={machine?.maintenanceIntervalHours || 250}
        isSaving={isSubmittingCustomConfig}
      />

      {/* AGC: Icon picker modal */}
      {machine && (
        <MachineIconPicker
          isOpen={showIconPicker}
          currentIconPath={machineIconSrc}
          initialCategory={machine.category}
          onClose={() => setShowIconPicker(false)}
          onSelect={async (selection) => {
            try {
              const token = await getAuthToken()
              const updates: Partial<typeof machine> = { customIconPath: selection.iconPath }
              if (selection.category !== normalizedCat) {
                updates.category = selection.category as any
              }
              updates.catalogId = selection.subtypeKey.replace(/_/g, '-')
              // AGC: Learn brand color when user picks icon for unknown brand
              if (machine.brand && !isKnownBrand(machine.brand)) {
                const colors = ['amarelo', 'azul', 'laranja', 'verde', 'vermelho', 'preto']
                const picked = colors.find(c => selection.iconPath.includes(`_${c}`))
                if (picked) (updates as any).brandColor = picked
              }
              await updateMachine(machineId, updates, token)
              setMachine(prev => prev ? { ...prev, ...updates } : prev)
              window.dispatchEvent(new CustomEvent('refresh-machines'))
            } catch (err) {
              console.error('AGC: Error updating icon:', err)
              toast.error('Erro ao atualizar ícone')
            }
          }}
        />
      )}
    </div>
  )
}

export default MachineDetail
